import { useEffect, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { usePublicFeed } from '../../hooks/usePublicFeed';
import { withBase } from '../../paths';

/**
 * Transient "new content just arrived" toast (ADR-0029). Driven by the public
 * SSE feed, so it shows for EVERY visitor (anonymous included) the moment a Work
 * or guide goes live. Ephemeral by design: it surfaces the latest coalesced
 * summary, auto-dismisses after a few seconds, and links to the relevant index.
 * Nothing is persisted - close the tab and it is gone.
 */
const VISIBLE_MS = 8000;

export function PublicFeedToast() {
    const feed = usePublicFeed();
    const location = useLocation();
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        if (feed.seq === 0 || !feed.latest) return;
        const { worksCount, guidesCount } = feed.latest;
        if (worksCount === 0 && guidesCount === 0) return;
        setVisible(true);
        const t = window.setTimeout(() => setVisible(false), VISIBLE_MS);
        return () => window.clearTimeout(t);
    }, [feed.seq]);

    if (!visible || !feed.latest) return null;

    const { worksCount, guidesCount } = feed.latest;
    const parts: string[] = [];
    if (worksCount > 0) parts.push(`${worksCount} new ${worksCount === 1 ? 'work' : 'works'}`);
    if (guidesCount > 0) parts.push(`${guidesCount} new ${guidesCount === 1 ? 'guide' : 'guides'}`);
    if (parts.length === 0) return null;

    const label = parts.join(' and ');
    const target = worksCount > 0 ? '/emporium' : '/articles';

    const go = () => {
        setVisible(false);
        location.route(withBase(target));
    };

    return (
        <div
            role="status"
            class="fixed bottom-4 right-4 z-50 flex max-w-[min(92vw,360px)] items-center gap-3 rounded-lg border border-rule bg-surface-base px-4 py-3 shadow-lg"
        >
            <button
                type="button"
                onClick={go}
                class="flex min-w-0 items-center gap-2 text-left text-[13px] font-medium text-fg-primary transition-colors hover:text-accent"
            >
                <span aria-hidden="true" class="h-2 w-2 flex-none rounded-full bg-accent" />
                <span class="truncate">{label} just added</span>
            </button>
            <button
                type="button"
                aria-label="Dismiss"
                onClick={() => setVisible(false)}
                class="grid h-6 w-6 flex-none place-items-center rounded text-fg-muted transition-colors hover:bg-surface-raised hover:text-fg-primary"
            >
                <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                >
                    <path d="M18 6 6 18" />
                    <path d="m6 6 12 12" />
                </svg>
            </button>
        </div>
    );
}
